import { useCallback, useMemo } from 'react'
import {
  getRentedEquipmentsTableDef,
  getExchangeRentedEquipmentsTableDef,
  billedContractServicesTableDef,
  excessContractServicesTableDef,
} from '../components/spreadsheet/tableDefinitions'
import { useSpreadsheetExtractor } from './useSpreadsheetExtractor'
import { parseCurrencyToNumber } from '../../../../utils/objectUtils'

export const useBillingExtractor = (billType) => {
  const { extractData } = useSpreadsheetExtractor()

  const rentedEquipmentsDef = useMemo(
    () => getRentedEquipmentsTableDef(billType),
    [billType]
  )
  const exchangeEquipmentsDef = useMemo(
    () => getExchangeRentedEquipmentsTableDef(7),
    []
  )

  const extractBillingData = useCallback(
    (univerAPI, currentBillType, formData = {}) => {
      const config = {
        tables: [],
        totals: [
          { key: 'totalGeral', label: 'TOTAL GERAL:' },
          { key: 'totalTipos', label: 'TOTAL:' },
          { key: 'totalExcedente', label: 'TOTAL EXCEDENTE:' },
          { key: 'totalFaturamento', label: 'TOTAL FATURAMENTO:' },
        ],
      }

      if (currentBillType === 'by_contract' || currentBillType === 'separate') {
        config.tables.push({
          name: 'rentedEquipments',
          searchTitle: 'TIPOS ALUGADOS',
          columns: ['tipo_equipamento', 'quantidade', 'preco_unitario'],
          useMetadata: true,
          tableDef: rentedEquipmentsDef,
          maxRows: 100,
        })

        // <-- Configuração para extrair a tabela de excedentes (apenas no modo contrato)
        if (currentBillType === 'by_contract') {
          config.tables.push({
            name: 'exchangeRentedEquipments',
            searchTitle: 'EXCEDENTES DO CONTRATO',
            columns: ['tipo_equipamento', 'quantidade', 'preco_unitario'],
            useMetadata: true,
            tableDef: exchangeEquipmentsDef,
            maxRows: 100,
          })

          config.tables.push({
            name: 'billedContractServices',
            searchTitle: 'SERVIÇOS FATURADOS',
            columns: ['service_id', 'alias', 'description', 'quantity', 'price'],
            useMetadata: true,
            tableDef: billedContractServicesTableDef,
            maxRows: 100,
          })

          config.tables.push({
            name: 'excessContractServices',
            searchTitle: 'EXCEDENTES DE SERVIÇOS',
            columns: ['service_id', 'alias', 'description', 'quantity', 'price'],
            useMetadata: true,
            tableDef: excessContractServicesTableDef,
            maxRows: 100,
          })
        }
      }

      const result = extractData(univerAPI, config, {
        formData: {
          contractItems: formData.items || [],
          rentedEquipments: formData.items || [],
        },
      })

      const items = []

      // Extrai itens normais
      if (result.tables.rentedEquipments) {
        result.tables.rentedEquipments.forEach((item) => {
          items.push({
            alias: item.tipo_equipamento || '',
            description: item.tipo_equipamento || '',
            quantity: parseInt(item.quantidade) || 0,
            price: parseCurrencyToNumber(item.preco_unitario),
            is_exchange: false,
          })
        })
      }

      // Extrai itens excedentes e os adiciona à lista de faturamento
      if (
        currentBillType === 'by_contract' &&
        result.tables.exchangeRentedEquipments
      ) {
        result.tables.exchangeRentedEquipments.forEach((item) => {
          items.push({
            alias: item.tipo_equipamento || '',
            description: `${item.tipo_equipamento} (EXCEDENTE)`,
            quantity: parseInt(item.quantidade) || 0,
            price: parseCurrencyToNumber(item.preco_unitario),
            is_exchange: true,
          })
        })
      }

      if (currentBillType === 'by_contract' && result.tables.billedContractServices) {
        result.tables.billedContractServices.forEach((service) => {
          items.push({
            alias: `SERVIÇO - ${service.alias || service.service_id}`,
            description: service.description || service.alias || '',
            quantity: Math.max(0, parseInt(service.quantity, 10) || 0),
            price: parseCurrencyToNumber(service.price),
          })
        })
      }

      if (currentBillType === 'by_contract' && result.tables.excessContractServices) {
        result.tables.excessContractServices.forEach((service) => {
          const quantity = Math.max(0, parseInt(service.quantity, 10) || 0)
          if (quantity === 0) return

          items.push({
            alias: `SERVIÇO - EXCEDENTE - ${service.alias || service.service_id}`,
            description: `${service.description || service.alias || ''} (EXCEDENTE DE CONTRATO)`,
            quantity,
            price: parseCurrencyToNumber(service.price),
          })
        })
      }

      return {
        items,
        totals: result.totals,
        metadata: {
          ...result.metadata,
          billType: currentBillType,
          itemCount: items.length,
        },
      }
    },
    [extractData, rentedEquipmentsDef, exchangeEquipmentsDef]
  )

  return { extractBillingData }
}
